import type { ReactNode } from "react";
import { Spinner } from "./Spinner";
import { Card } from "./Card";
import { EmptyState } from "./EmptyState";

export type Column<T> = {
  header: string;
  // Pass an `align: "right"` to right-justify (e.g. amounts).
  align?: "left" | "right";
  // Render the cell. Receives the row.
  render: (row: T) => ReactNode;
  // Optional fixed width (Tailwind class).
  width?: string;
};

export function DataTable<T>({
  rows,
  loading,
  columns,
  rowKey,
  rowActions,
  empty,
}: {
  rows: T[] | null;
  loading: boolean;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  rowActions?: (row: T) => ReactNode;
  empty: { title: string; description?: string; action?: ReactNode };
}) {
  if (loading && !rows) {
    return (
      <Card className="py-16 flex items-center justify-center">
        <Spinner />
      </Card>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <Card>
        <EmptyState {...empty} />
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-ink-3 ${c.align === "right" ? "text-right" : "text-left"} ${c.width ?? ""}`}
                >
                  {c.header}
                </th>
              ))}
              {rowActions && <th className="px-4 py-3 w-px" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-b border-border last:border-0 hover:bg-surface-2/50"
              >
                {columns.map((c, i) => (
                  <td
                    key={i}
                    className={`px-4 py-3 text-ink ${c.align === "right" ? "text-right" : ""}`}
                  >
                    {c.render(row)}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {rowActions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
